// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Queue from '@segment/localstorage-retry?client';

/**
 * Queue options
 *
 * Attempt with exponential backoff for upto 10 times.
 * Backoff periods are: 1s, 2s, 4s, 8s, 16s, 32s, 64s, 128s (~2m), 256s (~4m),
 * 512s (~8.5m) and 1024s (~17m).
 */
export const queueOptions = {
    maxRetryDelay: 360000, // max interval of 1hr. Added as a guard.
    minRetryDelay: 1000, // first attempt (1s)
    backoffFactor: 2,
    maxAttempts: 10,
    maxItems: 100,
};

let queue: Queue = null;

export const getQueue = () => {
    if (!queue) {
        queue = initializeQueue();
    }

    return queue;
};

export const initializeQueue = () => {
    const queue = new Queue('perspective', queueOptions, queueExecute);

    queue.on('processed', (err: { status: string; responseText: string }, res: any, item: any) => {
        if (err) {
            const lastPart = item.url.split('/').reverse()[0];

            console.warn(`Tracking Error: /${lastPart} - ${err.status}`, {
                data: {
                    ...item,
                    error: JSON.stringify(err),
                    status: err.status,
                    responseText: err.responseText,
                },
            });
        }
    });

    queue.shouldRetry = function (
        item: {
            message: {
                messageId: string;
            };
            url: string;
        },
        attemptNumber: number,
    ) {
        const storedQueue = this._store.get(this.keys.QUEUE) || [];
        const found = storedQueue.find((queueContent: { [key: string]: any }) => {
            return queueContent.item.message.messageId === item.message.messageId;
        });

        if (found) {
            return false;
        }

        if (attemptNumber > 0 && item.url[0] === '/') {
            item.url = import.meta.env.VITE_TRACKING_API_FALLBACK + item.url;
        }

        return attemptNumber <= this.maxAttempts;
    };

    queue.start();

    return queue;
};

export const queueExecute = (
    item: {
        message: {
            sentAt: string;
        };
        url: string;
    },
    done: (req: XMLHttpRequest | null, ev?: ProgressEvent<EventTarget>) => void,
) => {
    // apply sentAt at flush time and reset on each retry
    // so the tracking-api doesn't interpret a time skew
    item.message.sentAt = new Date().toISOString();

    send(item.message, item.url, (req: XMLHttpRequest, res: ProgressEvent) => {
        if (req) {
            return done(req);
        }
        return done(null, res);
    });
};

export const send = (body: any, url: string, onFail: any, timeout = 10000) => {
    // use XMLHttpRequest because of browser support and backwards compatibility
    // https://caniuse.com/?search=XMLHttpRequest
    const req = new XMLHttpRequest();

    const done = () => {
        // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/readyState
        // when operation is complete
        if (req.readyState === 4) {
            if (req.status !== 200) {
                onFail(req);
            } else {
                onFail(null, req);
            }
        }
    };

    req.onerror = onFail;
    req.onreadystatechange = done;
    req.open('POST', url, true);

    req.timeout = timeout;
    req.ontimeout = onFail;

    req.setRequestHeader('Content-Type', 'text/plain');
    req.send(JSON.stringify(body));
};
